import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Edit, Trash2, X, Save, Clock, AlertCircle, Loader2, Search, Filter, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';
import { shipmentService } from '../services/shipmentService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { exportRowsToExcel } from '../utils/excelExport';

interface WaitingRecord {
  id: string;
  shipmentId?: string;
  warehouseId?: string;
  orderNumber: string;
  vehiclePlate: string;
  driverName: string;
  location: string;
  locationType: 'Yükleme' | 'Teslimat' | 'Gümrük' | 'Araç Bakım';
  waitingReason: string;
  startTime: string;
  endTime: string;
  duration: string;
  cost: string;
  status: 'Devam Ediyor' | 'Tamamlandı' | 'Onay Bekliyor';
  notes: string;
  isChargeable: boolean;
  reporterRole?: string;
  issueCategory?: string;
  latitude?: number | null;
  longitude?: number | null;
  reportedAt?: string;
  resolvedAt?: string;
  source?: string;
  processPhase?: string;
  operationStartedAt?: string;
  operationCompletedAt?: string;
  ticketStatus?: 'open' | 'in-progress' | 'resolved';
  queueTeam?: 'operation' | 'customer-service';
  firstResponseMinutes?: number;
  resolutionMinutes?: number;
}

type WaitingStatus = WaitingRecord['status'];
type WaitingLocationType = WaitingRecord['locationType'];

const waitingReasons = [
  'Yükleme Hazırlığı',
  'Boşaltma İşlemi',
  'Gümrük İşlemleri',
  'Evrak Eksikliği',
  'Araç Bakım/Arıza',
  'Trafik/Yol Durumu',
  'Müşteri Talebi',
  'Hava Koşulları',
  'Diğer',
];

const defaultFormData: Partial<WaitingRecord> = {
  status: 'Devam Ediyor',
  locationType: 'Yükleme',
  isChargeable: false,
  duration: '0',
  cost: '0',
};

const normalizeWaitingStatus = (status?: string): WaitingStatus => {
  switch (status) {
    case 'completed':
    case 'Tamamlandı':
      return 'Tamamlandı';
    case 'pending_approval':
    case 'Onay Bekliyor':
      return 'Onay Bekliyor';
    default:
      return 'Devam Ediyor';
  }
};

const normalizeLocationType = (type?: string): WaitingLocationType => {
  switch (type) {
    case 'Teslimat':
    case 'delivery':
      return 'Teslimat';
    case 'Gümrük':
    case 'gumruk':
    case 'customs':
      return 'Gümrük';
    case 'Araç Bakım':
    case 'vehicle_maintenance':
    case 'maintenance':
      return 'Araç Bakım';
    default:
      return 'Yükleme';
  }
};

const toDateTimeLocal = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 16);
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const mapWaitingRecord = (item: any): WaitingRecord => ({
  id: String(item.id),
  shipmentId: item.shipment_id ? String(item.shipment_id) : '',
  warehouseId: item.warehouse_id ? String(item.warehouse_id) : '',
  orderNumber: item.order_number || item.shipment_no || '',
  vehiclePlate: item.vehicle_plate || '',
  driverName: item.driver_name || '',
  location: item.location || item.warehouse_name || '',
  locationType: normalizeLocationType(item.waiting_type || item.location_type),
  waitingReason: item.reason || '',
  startTime: toDateTimeLocal(item.start_time || ''),
  endTime: toDateTimeLocal(item.end_time || ''),
  duration: String(item.duration_minutes || 0),
  cost: String(item.cost || 0),
  status: normalizeWaitingStatus(item.status),
  notes: item.notes || '',
  isChargeable: Boolean(item.is_chargeable),
  reporterRole: item.reporter_role || '',
  issueCategory: item.issue_category || '',
  latitude: item.latitude != null ? Number(item.latitude) : null,
  longitude: item.longitude != null ? Number(item.longitude) : null,
  reportedAt: toDateTimeLocal(item.reported_at || item.start_time || ''),
  resolvedAt: toDateTimeLocal(item.resolved_at || ''),
  source: item.source || '',
  processPhase: item.process_phase || '',
  operationStartedAt: toDateTimeLocal(item.operation_started_at || ''),
  operationCompletedAt: toDateTimeLocal(item.operation_completed_at || ''),
  ticketStatus: item.ticket_status || 'open',
  queueTeam: item.queue_team || (item.issue_category === 'unloading-problem' ? 'customer-service' : 'operation'),
  firstResponseMinutes: item.first_response_minutes != null ? Number(item.first_response_minutes) : undefined,
  resolutionMinutes: item.resolution_minutes != null ? Number(item.resolution_minutes) : undefined,
});

export function WaitingRecords() {
  const [data, setData] = useState<WaitingRecord[]>([]);
  const [operationQueue, setOperationQueue] = useState<WaitingRecord[]>([]);
  const [customerServiceQueue, setCustomerServiceQueue] = useState<WaitingRecord[]>([]);
  const [slaSummary, setSlaSummary] = useState<any>({
    total_tickets: 0,
    open_tickets: 0,
    in_progress_tickets: 0,
    resolved_tickets: 0,
    avg_first_response_minutes: 0,
    avg_resolution_minutes: 0,
  });
  const [shipments, setShipments] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WaitingRecord | null>(null);
  const [formData, setFormData] = useState<Partial<WaitingRecord>>(defaultFormData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [highlightedRows, setHighlightedRows] = useState<Record<string, { expiresAt: number; type: 'new' | 'updated'; fields: string[] }>>({});
  const previousRowsRef = useRef<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState<'none' | 'status' | 'locationType' | 'queueTeam' | 'ticketStatus' | 'processPhase'>('none');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [filters, setFilters] = useState<Partial<Record<'status' | 'locationType' | 'queueTeam' | 'ticketStatus' | 'processPhase' | 'source', string>>>({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadData();
    }, 15000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const currentTime = Date.now();
      setHighlightedRows((current) => {
        const next = Object.fromEntries(
          Object.entries(current).filter(([, meta]) => meta.expiresAt > currentTime)
        );
        return Object.keys(next).length === Object.keys(current).length ? current : next;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [recordsRes, shipmentsRes, warehousesRes, driversRes, vehiclesRes, opQueueRes, csQueueRes, slaRes] = await Promise.all([
        warehouseService.getWaitingRecords(),
        shipmentService.getAll(),
        warehouseService.getWarehouses(),
        driverService.getDrivers(),
        vehicleService.getVehicles(),
        warehouseService.getWaitingQueue({ team: 'operation' }),
        warehouseService.getWaitingQueue({ team: 'customer-service' }),
        warehouseService.getWaitingSlaSummary(),
      ]);

      const nextData = recordsRes.success && recordsRes.data ? recordsRes.data.map(mapWaitingRecord) : [];
      const nextSnapshot: Record<string, string> = {};
      const rowMeta: Record<string, { type: 'new' | 'updated'; fields: string[] }> = {};

      nextData.forEach((item) => {
        const trackedFields: Record<string, unknown> = {
          durum: item.status,
          neden: item.waitingReason,
          beklemeBaslangici: item.startTime,
          beklemeBitisi: item.endTime,
          islemBaslama: item.operationStartedAt,
          islemBitis: item.operationCompletedAt,
          sureDakika: item.duration,
          maliyet: item.cost,
          ticket: item.ticketStatus,
          kuyruk: item.queueTeam,
          koordinat: item.latitude != null && item.longitude != null ? `${item.latitude},${item.longitude}` : '',
          not: item.notes,
        };
        const signature = JSON.stringify(trackedFields);
        nextSnapshot[item.id] = signature;
        if (!previousRowsRef.current[item.id]) {
          rowMeta[item.id] = { type: 'new', fields: ['yeni kayıt'] };
          return;
        }
        if (previousRowsRef.current[item.id] !== signature) {
          let previousFields: Record<string, unknown> = {};
          try {
            previousFields = JSON.parse(previousRowsRef.current[item.id]);
          } catch {
            previousFields = {};
          }
          const changedFields = Object.keys(trackedFields).filter((key) => previousFields[key] !== trackedFields[key]);
          rowMeta[item.id] = { type: 'updated', fields: changedFields };
        }
      });

      previousRowsRef.current = nextSnapshot;
      const changedEntries = Object.entries(rowMeta);
      if (changedEntries.length > 0) {
        const expiresAt = Date.now() + 10000;
        setHighlightedRows((current) => ({
          ...current,
          ...Object.fromEntries(changedEntries.map(([id, meta]) => [id, { ...meta, expiresAt }])),
        }));
      }

      setData(nextData);
      setShipments(shipmentsRes.success && shipmentsRes.data ? shipmentsRes.data : []);
      setWarehouses(warehousesRes.success && warehousesRes.data ? warehousesRes.data : []);
      setDrivers(driversRes.success && driversRes.data ? driversRes.data : []);
      setVehicles(vehiclesRes.success && vehiclesRes.data ? vehiclesRes.data : []);
      setOperationQueue(opQueueRes?.success && opQueueRes?.data ? opQueueRes.data.map(mapWaitingRecord) : []);
      setCustomerServiceQueue(csQueueRes?.success && csQueueRes?.data ? csQueueRes.data.map(mapWaitingRecord) : []);
      setSlaSummary(slaRes?.success && slaRes?.data?.summary ? slaRes.data.summary : {
        total_tickets: 0,
        open_tickets: 0,
        in_progress_tickets: 0,
        resolved_tickets: 0,
        avg_first_response_minutes: 0,
        avg_resolution_minutes: 0,
      });
    } catch (err: any) {
      console.error('Error loading waiting records:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { key: 'orderNumber', label: 'İş Emri' },
    { key: 'source', label: 'Kaynak' },
    { key: 'queueTeam', label: 'Queue' },
    { key: 'ticketStatus', label: 'Ticket' },
    { key: 'vehiclePlate', label: 'Plaka' },
    { key: 'driverName', label: 'Sürücü' },
    { key: 'reporterRole', label: 'Bildiren Rol' },
    { key: 'location', label: 'Lokasyon' },
    { key: 'locationType', label: 'Tip' },
    { key: 'issueCategory', label: 'Kategori' },
    { key: 'waitingReason', label: 'Bekleme Nedeni' },
    { key: 'startTime', label: 'Bekleme Başlangıcı' },
    { key: 'operationStartedAt', label: 'İşlem Başlama' },
    { key: 'operationCompletedAt', label: 'İşlem Bitiş' },
    { key: 'reportedAt', label: 'Bildirim Zamanı' },
    { key: 'coordinates', label: 'Koordinat' },
    { key: 'duration', label: 'Süre (dk)' },
    { key: 'cost', label: 'Maliyet (₺)' },
    { key: 'status', label: 'Durum' },
  ];

  const openForm = (item?: WaitingRecord) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData(defaultFormData);
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData(defaultFormData);
  };

  const calculateDuration = (start: string, end: string) => {
    if (!start || !end) return 0;
    return Math.max(0, Math.floor((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60)));
  };

  const calculateCost = (duration: number) => (duration * 5).toFixed(2);

  const buildPayload = () => {
    const duration = formData.endTime ? calculateDuration(formData.startTime || '', formData.endTime) : 0;
    const cost = formData.isChargeable ? calculateCost(duration) : '0';

    return {
      shipmentId: formData.shipmentId || null,
      warehouseId: formData.warehouseId || null,
      orderNumber: formData.orderNumber || '',
      vehiclePlate: formData.vehiclePlate || '',
      driverName: formData.driverName || '',
      location: formData.location || '',
      locationType: formData.locationType || 'Yükleme',
      waitingReason: formData.waitingReason || '',
      startTime: formData.startTime || '',
      endTime: formData.endTime || null,
      duration,
      cost,
      status: formData.status || 'Devam Ediyor',
      notes: formData.notes || '',
      isChargeable: Boolean(formData.isChargeable),
    };
  };

  const handleShipmentChange = (shipmentId: string) => {
    const shipment = shipments.find((item) => String(item.id) === shipmentId);
    setFormData({
      ...formData,
      shipmentId,
      orderNumber: shipment?.shipment_no || '',
      warehouseId: shipment?.pickup_warehouse_id ? String(shipment.pickup_warehouse_id) : formData.warehouseId,
      location: shipment?.pickup_warehouse_name || formData.location || '',
      locationType: 'Yükleme',
    });
  };

  const handleWarehouseChange = (warehouseId: string) => {
    const warehouse = warehouses.find((item) => String(item.id) === warehouseId);
    setFormData({
      ...formData,
      warehouseId,
      location: warehouse?.name || '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const payload = buildPayload();
      const response = editingItem
        ? await warehouseService.updateWaitingRecord(editingItem.id, payload)
        : await warehouseService.createWaitingRecord(payload);

      if (!response.success) {
        throw new Error(response.message || 'Bekleme kaydı kaydedilemedi');
      }

      await loadData();
      closeForm();
    } catch (err: any) {
      console.error('Error saving waiting record:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bu bekleme kaydını silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await warehouseService.deleteWaitingRecord(id);
      if (!response.success) {
        throw new Error(response.message || 'Bekleme kaydı silinemedi');
      }
      await loadData();
    } catch (err: any) {
      console.error('Error deleting waiting record:', err);
      alert(err.message || 'Silme sırasında hata oluştu');
    }
  };

  const renderCell = (item: WaitingRecord, key: string) => {
    if (key === 'status') {
      const colors = {
        'Devam Ediyor': 'bg-yellow-100 text-yellow-800',
        'Tamamlandı': 'bg-green-100 text-green-800',
        'Onay Bekliyor': 'bg-orange-100 text-orange-800',
      };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.status]}`}>{item.status}</span>;
    }
    if (key === 'locationType') {
      const colors = {
        'Yükleme': 'bg-blue-100 text-blue-800',
        'Teslimat': 'bg-purple-100 text-purple-800',
        'Gümrük': 'bg-orange-100 text-orange-800',
        'Araç Bakım': 'bg-gray-100 text-gray-800',
      };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.locationType]}`}>{item.locationType}</span>;
    }
    if (key === 'location') {
      return <div className="max-w-[200px] truncate" title={item.location}>{item.location}</div>;
    }
    if (key === 'source') {
      const value = item.source || '-';
      return <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{value}</span>;
    }
    if (key === 'queueTeam') {
      const value = item.queueTeam === 'customer-service' ? 'Müşteri Hizmetleri' : 'Operasyon';
      const color = item.queueTeam === 'customer-service' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700';
      return <span className={`text-xs px-2 py-1 rounded ${color}`}>{value}</span>;
    }
    if (key === 'ticketStatus') {
      const value = item.ticketStatus || 'open';
      const map: Record<string, { label: string; className: string }> = {
        open: { label: 'Open', className: 'bg-rose-100 text-rose-700' },
        'in-progress': { label: 'In Progress', className: 'bg-amber-100 text-amber-700' },
        resolved: { label: 'Resolved', className: 'bg-emerald-100 text-emerald-700' },
      };
      const ui = map[value] || map.open;
      return <span className={`text-xs px-2 py-1 rounded ${ui.className}`}>{ui.label}</span>;
    }
    if (key === 'issueCategory') {
      const value = item.issueCategory || '-';
      const color = value === 'unloading-problem' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700';
      return <span className={`text-xs px-2 py-1 rounded ${color}`}>{value}</span>;
    }
    if (key === 'operationStartedAt' || key === 'operationCompletedAt' || key === 'startTime' || key === 'reportedAt') {
      return item[key as keyof WaitingRecord] || '-';
    }
    if (key === 'coordinates') {
      if (item.latitude == null || item.longitude == null) return '-';
      return (
        <a
          href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 hover:underline text-xs"
        >
          {item.latitude.toFixed(5)}, {item.longitude.toFixed(5)}
        </a>
      );
    }
    if (key === 'duration') {
      return String(getLiveDurationMinutes(item));
    }
    if (key === 'cost' && item.isChargeable) {
      return <span className="text-red-600 font-medium">{item.cost} ₺</span>;
    }
    return item[key as keyof WaitingRecord] || '-';
  };

  const updateTicketStatus = async (item: WaitingRecord, nextStatus: 'open' | 'in-progress' | 'resolved') => {
    try {
      const response = await warehouseService.updateWaitingTicketStatus(item.id, {
        ticketStatus: nextStatus,
        queueTeam: item.queueTeam,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Ticket durumu güncellenemedi');
      }
      await loadData();
    } catch (err: any) {
      console.error('Ticket status update error:', err);
      alert(err?.message || 'Ticket durumu güncellenemedi');
    }
  };

  const renderActions = (item: WaitingRecord) => (
    <div className="flex gap-2">
      {item.ticketStatus !== 'in-progress' && (
        <button onClick={() => updateTicketStatus(item, 'in-progress')} className="px-2 py-1 text-xs text-amber-700 bg-amber-100 rounded hover:bg-amber-200">In Progress</button>
      )}
      {item.ticketStatus !== 'resolved' && (
        <button onClick={() => updateTicketStatus(item, 'resolved')} className="px-2 py-1 text-xs text-emerald-700 bg-emerald-100 rounded hover:bg-emerald-200">Resolve</button>
      )}
      <button onClick={() => openForm(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
      <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  const getLiveDurationMinutes = (item: WaitingRecord) => {
    if (item.status === 'Devam Ediyor' && item.startTime) {
      return calculateDuration(item.startTime, new Date(now).toISOString());
    }
    return parseInt(item.duration || '0', 10);
  };

  const totalWaitingCost = data.reduce((sum, item) => sum + parseFloat(item.cost || '0'), 0);
  const totalLoadingWaitingTime = data
    .filter((item) => item.locationType === 'Yükleme')
    .reduce((sum, item) => sum + getLiveDurationMinutes(item), 0);
  const totalUnloadingWaitingTime = data
    .filter((item) => item.locationType === 'Teslimat')
    .reduce((sum, item) => sum + getLiveDurationMinutes(item), 0);

  const filteredData = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase('tr-TR');
    return data.filter((item) => {
      if (filters.status && item.status !== filters.status) return false;
      if (filters.locationType && item.locationType !== filters.locationType) return false;
      if (filters.queueTeam && (item.queueTeam || '') !== filters.queueTeam) return false;
      if (filters.ticketStatus && (item.ticketStatus || '') !== filters.ticketStatus) return false;
      if (filters.processPhase && (item.processPhase || '') !== filters.processPhase) return false;
      if (filters.source && (item.source || '') !== filters.source) return false;
      if (!query) return true;

      const haystack = [
        item.orderNumber,
        item.vehiclePlate,
        item.driverName,
        item.location,
        item.waitingReason,
        item.locationType,
        item.status,
        item.source,
        item.processPhase,
      ].join(' ').toLocaleLowerCase('tr-TR');
      return haystack.includes(query);
    });
  }, [data, filters, searchTerm]);

  const groupedData = useMemo(() => {
    if (groupBy === 'none') return [] as Array<{ key: string; label: string; items: WaitingRecord[] }>;
    const map = new Map<string, WaitingRecord[]>();
    filteredData.forEach((item) => {
      const key = String(item[groupBy as keyof WaitingRecord] || '-');
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    });
    return Array.from(map.entries()).map(([key, items]) => ({ key, label: key, items }));
  }, [filteredData, groupBy]);

  const handleExportExcel = () => exportRowsToExcel(filteredData as any[], `waiting-records-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'WaitingRecords' });

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({});
    setGroupBy('none');
    setCollapsedGroups({});
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bekleme Kayıtları</h1>
          <p className="text-gray-500 mt-1">Araç bekleme sürelerini kaydedin ve maliyetleri hesaplayın</p>
        </div>
        <button onClick={() => openForm()} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          <Plus className="w-5 h-5" />
          Yeni Kayıt
        </button>
      </div>

      {isLoading && (
        <div className="bg-white rounded-lg border border-gray-200 py-16 flex justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-500">Bekleme kayıtları yükleniyor...</p>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="bg-white rounded-lg border border-red-200 py-12 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Tekrar Dene</button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Toplam Kayıt</div><div className="text-2xl font-bold text-gray-900 mt-1">{data.length}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Devam Eden</div><div className="text-2xl font-bold text-yellow-600 mt-1">{data.filter((record) => record.status === 'Devam Ediyor').length}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Toplam Yükleme Beklemesi</div><div className="text-2xl font-bold text-blue-600 mt-1">{Math.floor(totalLoadingWaitingTime / 60)}s {totalLoadingWaitingTime % 60}dk</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Toplam Boşaltma Beklemesi</div><div className="text-2xl font-bold text-purple-600 mt-1">{Math.floor(totalUnloadingWaitingTime / 60)}s {totalUnloadingWaitingTime % 60}dk</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Toplam Maliyet</div><div className="text-2xl font-bold text-red-600 mt-1">{totalWaitingCost.toLocaleString('tr-TR')} ₺</div></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Open Ticket</div><div className="text-2xl font-bold text-rose-600 mt-1">{Number(slaSummary.open_tickets || 0)}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">In Progress</div><div className="text-2xl font-bold text-amber-600 mt-1">{Number(slaSummary.in_progress_tickets || 0)}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Resolved</div><div className="text-2xl font-bold text-emerald-600 mt-1">{Number(slaSummary.resolved_tickets || 0)}</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Ort. İlk Yanıt</div><div className="text-2xl font-bold text-indigo-600 mt-1">{Number(slaSummary.avg_first_response_minutes || 0).toFixed(0)} dk</div></div>
            <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Ort. Çözüm</div><div className="text-2xl font-bold text-violet-600 mt-1">{Number(slaSummary.avg_resolution_minutes || 0).toFixed(0)} dk</div></div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4 border border-cyan-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Operasyon Queue</h3>
                <span className="text-xs px-2 py-1 rounded bg-cyan-100 text-cyan-700">{operationQueue.length} kayıt</span>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {operationQueue.slice(0, 20).map((item) => (
                  <div key={`op-${item.id}`} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-gray-900">{item.orderNumber || '-'}</div>
                      {renderCell(item, 'ticketStatus')}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{item.waitingReason || '-'}</div>
                    <div className="text-xs text-gray-500 mt-1">{item.driverName || '-'} • {item.vehiclePlate || '-'}</div>
                  </div>
                ))}
                {operationQueue.length === 0 && <div className="text-sm text-gray-500">Operasyon queue boş.</div>}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-violet-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Müşteri Hizmetleri Queue</h3>
                <span className="text-xs px-2 py-1 rounded bg-violet-100 text-violet-700">{customerServiceQueue.length} kayıt</span>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {customerServiceQueue.slice(0, 20).map((item) => (
                  <div key={`cs-${item.id}`} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-gray-900">{item.orderNumber || '-'}</div>
                      {renderCell(item, 'ticketStatus')}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{item.waitingReason || '-'}</div>
                    <div className="text-xs text-gray-500 mt-1">{item.driverName || '-'} • {item.vehiclePlate || '-'}</div>
                  </div>
                ))}
                {customerServiceQueue.length === 0 && <div className="text-sm text-gray-500">Müşteri hizmetleri queue boş.</div>}
              </div>
            </div>
          </div>

          {data.filter((record) => record.status === 'Onay Bekliyor').length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-orange-900">Onay Bekleyen Kayıtlar</h3>
                <p className="text-sm text-orange-700 mt-1">{data.filter((record) => record.status === 'Onay Bekliyor').length} adet bekleme kaydı onay bekliyor.</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="İş emri, plaka, sürücü, lokasyon ara"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setShowFilters((v) => !v)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"><Filter className="h-4 w-4" />Filtreler</button>
                <button onClick={() => setShowGrouping((v) => !v)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Gruplama</button>
                <button onClick={clearFilters} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"><XCircle className="h-4 w-4" />Temizle</button>
                <button onClick={handleExportExcel} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Excele Aktar</button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <select value={filters.status || ''} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value || undefined }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Tüm Durumlar</option>
                  <option value="Devam Ediyor">Devam Ediyor</option>
                  <option value="Tamamlandı">Tamamlandı</option>
                  <option value="Onay Bekliyor">Onay Bekliyor</option>
                </select>
                <select value={filters.locationType || ''} onChange={(e) => setFilters((current) => ({ ...current, locationType: e.target.value || undefined }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Tüm Tipler</option>
                  <option value="Yükleme">Yükleme</option>
                  <option value="Teslimat">Teslimat</option>
                  <option value="Gümrük">Gümrük</option>
                  <option value="Araç Bakım">Araç Bakım</option>
                </select>
                <select value={filters.queueTeam || ''} onChange={(e) => setFilters((current) => ({ ...current, queueTeam: e.target.value || undefined }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Tüm Queue</option>
                  <option value="operation">Operasyon</option>
                  <option value="customer-service">Müşteri Hizmetleri</option>
                </select>
                <select value={filters.ticketStatus || ''} onChange={(e) => setFilters((current) => ({ ...current, ticketStatus: e.target.value || undefined }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Tüm Ticket</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
                <select value={filters.processPhase || ''} onChange={(e) => setFilters((current) => ({ ...current, processPhase: e.target.value || undefined }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Tüm Fazlar</option>
                  <option value="loading">Yükleme</option>
                  <option value="unloading">Boşaltma</option>
                </select>
                <select value={filters.source || ''} onChange={(e) => setFilters((current) => ({ ...current, source: e.target.value || undefined }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Tüm Kaynaklar</option>
                  <option value="driver-mobile">Sürücü Mobil</option>
                  <option value="admin-tracking">Admin Tracking</option>
                </select>
              </div>
            )}

            {showGrouping && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Grupla:</span>
                <select value={groupBy} onChange={(e) => { setGroupBy(e.target.value as typeof groupBy); setCollapsedGroups({}); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="none">Gruplama Yok</option>
                  <option value="status">Durum</option>
                  <option value="locationType">Tip</option>
                  <option value="queueTeam">Queue</option>
                  <option value="ticketStatus">Ticket</option>
                  <option value="processPhase">Faz</option>
                </select>
                <span className="text-sm text-gray-500">{filteredData.length} kayıt</span>
              </div>
            )}
          </div>

          <div className="workiom-grid-wrap border border-gray-200 bg-white">
            <table className="workiom-grid-table text-sm">
                <thead>
                  <tr>
                    {columns.map((column) => <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{column.label}</th>)}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {groupBy === 'none' ? filteredData.map((item) => {
                      const highlightMeta = highlightedRows[item.id];
                      const rowClassName = highlightMeta
                        ? highlightMeta.type === 'new'
                          ? 'bg-emerald-50 transition-colors'
                          : 'bg-amber-50 transition-colors'
                        : 'hover:bg-gray-50';
                      const title = highlightMeta
                        ? highlightMeta.type === 'new'
                          ? 'Yeni kayıt'
                          : `Değişen alanlar: ${highlightMeta.fields.join(', ')}`
                        : undefined;
                      return (
                      <tr key={item.id} className={rowClassName} title={title}>
                        {columns.map((column) => <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{renderCell(item, column.key)}</td>)}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{renderActions(item)}</td>
                      </tr>
                    )}) : groupedData.map((group) => (
                      [
                        <tr key={`group-${group.key}`} className="bg-slate-50">
                          <td colSpan={columns.length + 1} className="px-6 py-3">
                            <button type="button" onClick={() => toggleGroup(group.key)} className="flex w-full items-center justify-between text-left text-sm font-semibold text-slate-800">
                              <span>{group.label} <span className="ml-2 text-xs font-medium text-slate-500">({group.items.length})</span></span>
                              {collapsedGroups[group.key] ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                            </button>
                          </td>
                        </tr>,
                        ...(collapsedGroups[group.key] ? [] : group.items.map((item) => {
                          const highlightMeta = highlightedRows[item.id];
                          const rowClassName = highlightMeta
                            ? highlightMeta.type === 'new'
                              ? 'bg-emerald-50 transition-colors'
                              : 'bg-amber-50 transition-colors'
                            : 'hover:bg-gray-50';
                          const title = highlightMeta
                            ? highlightMeta.type === 'new'
                              ? 'Yeni kayıt'
                              : `Değişen alanlar: ${highlightMeta.fields.join(', ')}`
                            : undefined;
                          return (
                            <tr key={item.id} className={rowClassName} title={title}>
                              {columns.map((column) => <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{renderCell(item, column.key)}</td>)}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{renderActions(item)}</td>
                            </tr>
                          );
                        }))
                      ]
                    ))}
                </tbody>
              </table>
          </div>
        </>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Clock className="w-6 h-6" />{editingItem ? 'Bekleme Kaydı Düzenle' : 'Yeni Bekleme Kaydı'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Temel Bilgiler</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">İş Emri No</label>
                    <select value={formData.shipmentId || ''} onChange={(e) => handleShipmentChange(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {shipments.map((shipment) => <option key={shipment.id} value={String(shipment.id)}>{shipment.shipment_no}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Araç Plakası *</label>
                    <select required value={formData.vehiclePlate || ''} onChange={(e) => setFormData({ ...formData, vehiclePlate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {vehicles.map((vehicle) => {
                        const plate = vehicle.plate_number || vehicle.cekici_plaka || vehicle.plate || '';
                        return <option key={vehicle.id} value={plate}>{plate}</option>;
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sürücü Adı *</label>
                    <select required value={formData.driverName || ''} onChange={(e) => setFormData({ ...formData, driverName: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {drivers.map((driver) => {
                        const name = driver.full_name || driver.name || '';
                        return <option key={driver.id} value={name}>{name}</option>;
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lokasyon Tipi *</label>
                    <select required value={formData.locationType || 'Yükleme'} onChange={(e) => setFormData({ ...formData, locationType: e.target.value as WaitingRecord['locationType'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="Yükleme">Yükleme</option>
                      <option value="Teslimat">Teslimat</option>
                      <option value="Gümrük">Gümrük</option>
                      <option value="Araç Bakım">Araç Bakım</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lokasyon *</label>
                    <div className="grid grid-cols-2 gap-4">
                      <select value={formData.warehouseId || ''} onChange={(e) => handleWarehouseChange(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">Depo seçiniz</option>
                        {warehouses.map((warehouse) => <option key={warehouse.id} value={String(warehouse.id)}>{warehouse.name}</option>)}
                      </select>
                      <input type="text" required value={formData.location || ''} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Bekleme yapılan yer" />
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bekleme Nedeni *</label>
                    <select required value={formData.waitingReason || ''} onChange={(e) => setFormData({ ...formData, waitingReason: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {waitingReasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Zaman ve Maliyet</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Zamanı *</label>
                    <input type="datetime-local" required value={formData.startTime || ''} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Zamanı</label>
                    <input type="datetime-local" value={formData.endTime || ''} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={formData.isChargeable || false} onChange={(e) => setFormData({ ...formData, isChargeable: e.target.checked })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm font-medium text-gray-700">Müşteriye faturalanacak</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">Bekleme süresi müşteriye yansıtılacaksa işaretleyin (5 TL/dakika)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Durum *</label>
                    <select required value={formData.status || 'Devam Ediyor'} onChange={(e) => setFormData({ ...formData, status: e.target.value as WaitingRecord['status'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="Devam Ediyor">Devam Ediyor</option>
                      <option value="Tamamlandı">Tamamlandı</option>
                      <option value="Onay Bekliyor">Onay Bekliyor</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                    <textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} placeholder="Detaylı açıklama" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={closeForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="submit" disabled={isSaving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingItem ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
